import React from "react";

import { cn } from "../@shadcn/lib/utils";
import DotsLoader from "./DotsLoader";

export interface PrimaryButtonCustomization {
  backgroundColor?: string;
  textColor?: string;
  /** Only meaningful on `appearance="outline"`, where the border carries the accent. */
  borderColor?: string;
  borderRadius?: number | string;
  hoverBackgroundColor?: string;
}

export interface PrimaryButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "type"> {
  type?: "button" | "submit" | "reset";
  /** Label. Ignored when `children` is passed. */
  buttonText?: string;
  /**
   * Sizing/shape escape hatches. Pass classes written in the CONSUMING app's convention
   * (`h-12`, `rounded-lg`) — they replace the packaged defaults instead of stacking with them,
   * so there is no prefixed-vs-unprefixed specificity fight.
   */
  height?: string;
  width?: string;
  borderRadius?: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  appearance?: "solid" | "outline";
  hideButton?: boolean;
  containerClassName?: string;
  /** Rendered inside the button, before the label. */
  leftSlot?: React.ReactNode;
  /** Rendered inside the button, after the label — hidden while loading. */
  rightSlot?: React.ReactNode;
  /** Rendered under the button, e.g. the checkout trust badge. */
  footerSlot?: React.ReactNode;
  /** Merchant overrides applied inline, so they beat every class. */
  customization?: PrimaryButtonCustomization;
}

/**
 * The checkout CTA, packaged.
 *
 * Colours come from the merchant theme CSS vars the host app sets (`--flo-primary-dark-color`,
 * `--flo-primary-btn-text-color`), so this stays on-brand without a colour prop. The checkout-only
 * extras the app version hard-codes — trust badge, UPI icon strip — are slots here, so their assets
 * stay in the app.
 *
 * `apps/checkout-ui`'s local `components/common/buttons/PrimaryButton` is the older twin, still used
 * by ~40 files. Use THIS one in new code.
 */
const PrimaryButton = React.forwardRef<HTMLButtonElement, PrimaryButtonProps>(
  (
    {
      type = "button",
      buttonText = "Confirm",
      height,
      width,
      borderRadius,
      isDisabled = false,
      isLoading = false,
      appearance = "solid",
      hideButton = false,
      className,
      containerClassName,
      leftSlot,
      rightSlot,
      footerSlot,
      customization,
      children,
      ...rest
    },
    ref,
  ) => {
    const isOutline = appearance === "outline";
    const canHover = !isDisabled && !isLoading && Boolean(customization?.hoverBackgroundColor);

    return (
      <div
        className={cn(
          "ui-flex ui-w-full ui-flex-col ui-items-center ui-space-y-2",
          containerClassName,
        )}
      >
        <button
          ref={ref}
          type={type}
          disabled={isLoading || isDisabled}
          style={{
            backgroundColor: customization?.backgroundColor,
            borderColor: customization?.borderColor,
            borderRadius: customization?.borderRadius,
            ...(customization?.hoverBackgroundColor
              ? { ["--pb-hover-bg" as string]: customization.hoverBackgroundColor }
              : {}),
          }}
          className={cn(
            "ui-flex ui-flex-row ui-items-center ui-gap-2 ui-whitespace-nowrap ui-px-4 ui-py-1.5 ui-transition-all ui-duration-200",
            "ui-text-text-md-semibold ui-text-[var(--flo-primary-btn-text-color,#ffffff)]",
            isOutline
              ? "ui-border-2 ui-border-[var(--flo-primary-dark-color,#01cca7)] ui-bg-white ui-text-[var(--flo-primary-dark-color,#01cca7)]"
              : "ui-bg-[var(--flo-primary-dark-color,#01cca7)]",
            canHover && "hover:!ui-bg-[color-mix(in_srgb,var(--pb-hover-bg)_96%,black_4%)]",
            isDisabled ? "ui-cursor-not-allowed ui-opacity-50" : "ui-cursor-pointer",
            isLoading && "ui-cursor-not-allowed",
            hideButton && "ui-hidden",
            height ?? "ui-h-14",
            width ?? "ui-w-full",
            borderRadius ?? "ui-rounded-xl",
            rightSlot && !isLoading ? "ui-justify-between" : "ui-justify-center",
            className,
          )}
          {...rest}
        >
          {leftSlot}
          <span
            className="ui-flex ui-items-center ui-gap-2"
            style={{ color: customization?.textColor }}
          >
            {isLoading ? (
              <DotsLoader
                color={
                  customization?.textColor ?? (isOutline ? "var(--flo-primary-dark-color)" : undefined)
                }
              />
            ) : (
              (children ?? buttonText)
            )}
          </span>
          {!isLoading && rightSlot}
        </button>
        {footerSlot}
      </div>
    );
  },
);

PrimaryButton.displayName = "PrimaryButton";

export default PrimaryButton;
